import { useEffect, useMemo, useState } from 'react';
import { reconciliationAPI } from '../../api';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FormLabel } from '../ui/FormLabel';
import { Card, CardContent } from '../ui/Card';
import { AlertTriangle, CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { AllocationTargetPicker } from './AllocationTargetPicker';

/**
 * Split allocation modal (plan §4/§8).
 *
 * Lets the user fan one statement line out across N internal records --
 * e.g. one incoming payment that clears three invoices, or one BACS
 * run covering five supplier bills.
 *
 * Constraint (plan §9): sum of |allocated_amount| across rows must equal
 * |statement line amount|. The modal displays a running remainder so the
 * user can see when they're balanced before submitting.
 */
const MATCH_TYPES_ALL = [
  'invoice_payment',
  'purchase_payment',
  'internal_transfer',
  'credit_card_settlement',
  'loan_drawdown',
  'loan_repayment',
  'proprietor_drawing_out',
  'proprietor_contribution_in',
  'personal_reimbursement',
  'refund_customer',
  'refund_supplier',
  'bank_charge',
  'bank_interest',
  'manual_adjustment',
  'opening_balance',
  'other',
];

function formatMoney(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

function blankAllocation() {
  return {
    _key: Math.random().toString(36).slice(2),
    allocated_amount: '',
    target_type: 'bank_transaction',
    target_id: null,
    nominal_account_id: null,
    description: '',
  };
}

export function SplitAllocationModal({ open, onOpenChange, line, bankAccountId, onMatched }) {
  const lineAmount = Number(line?.amount ?? 0);
  const lineAbs = Math.abs(lineAmount);
  const isInflow = lineAmount > 0;

  const [matchType, setMatchType] = useState(
    isInflow ? 'invoice_payment' : 'purchase_payment'
  );
  const [matchDate, setMatchDate] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [allocations, setAllocations] = useState([blankAllocation(), blankAllocation()]);
  const [reconcileNow, setReconcileNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError('');
    setDone(null);
    setBusy(false);
    setReconcileNow(false);
    setMatchType(lineAmount >= 0 ? 'invoice_payment' : 'purchase_payment');
    setMatchDate(line?.transaction_date ? String(line.transaction_date).slice(0, 10) : '');
    setReference(line?.reference || '');
    setNotes('');
    setAllocations([blankAllocation(), blankAllocation()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line?.id]);

  const allocatedSum = useMemo(() => {
    return allocations.reduce((acc, a) => {
      const n = Number(a.allocated_amount);
      return acc + (Number.isFinite(n) ? Math.abs(n) : 0);
    }, 0);
  }, [allocations]);

  const remainder = round2(lineAbs - allocatedSum);
  const balanced = Math.abs(remainder) < 0.005;

  const updateAlloc = (key, patch) => {
    setAllocations((prev) => prev.map((a) => (a._key === key ? { ...a, ...patch } : a)));
  };

  const addAlloc = () => setAllocations((prev) => [...prev, blankAllocation()]);
  const removeAlloc = (key) =>
    setAllocations((prev) => (prev.length > 1 ? prev.filter((a) => a._key !== key) : prev));

  const fillRemainderOnLast = () => {
    if (allocations.length === 0) return;
    setAllocations((prev) => {
      const copy = [...prev];
      const last = { ...copy[copy.length - 1] };
      const rem = round2(lineAbs - prev.slice(0, -1).reduce((acc, a) => {
        const n = Number(a.allocated_amount);
        return acc + (Number.isFinite(n) ? Math.abs(n) : 0);
      }, 0));
      last.allocated_amount = rem > 0 ? String(rem) : '';
      copy[copy.length - 1] = last;
      return copy;
    });
  };

  const canSubmit = useMemo(() => {
    if (!line || !bankAccountId || !matchType) return false;
    if (!balanced) return false;
    for (const a of allocations) {
      const n = Number(a.allocated_amount);
      if (!Number.isFinite(n) || n <= 0) return false;
      if (a.target_type === 'nominal_category') {
        if (!a.nominal_account_id) return false;
      } else if (!a.target_id) {
        return false;
      }
    }
    return true;
  }, [line, bankAccountId, matchType, balanced, allocations]);

  const submit = async () => {
    if (!canSubmit) return;
    setError('');
    setBusy(true);
    try {
      const created = await reconciliationAPI.createMatch({
        bank_account_id: bankAccountId,
        match_type: matchType,
        match_strategy: 'manual',
        total_amount: lineAbs,
        match_date: matchDate || undefined,
        reference: reference || null,
        notes: notes || null,
        allocations: allocations.map((a) => ({
          statement_line_id: line.id,
          allocated_amount: Number(a.allocated_amount),
          target_type: a.target_type,
          target_id: a.target_id || null,
          nominal_account_id: a.nominal_account_id || null,
          description: a.description || null,
        })),
      });

      if (reconcileNow) {
        await reconciliationAPI.reconcileMatch(created.match.id);
      }

      setDone({ match: created.match, reconciled: reconcileNow });
      if (onMatched) onMatched(created.match);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not create match');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver
      open={open}
      onOpenChange={onOpenChange}
      title="Split statement line across targets"
      width="w-full max-w-3xl"
    >
      {!line ? (
        <div className="text-xs text-gray-500">No statement line selected.</div>
      ) : (
        <div className="space-y-4">
          {/* Line + running total */}
          <Card>
            <CardContent className="p-3 text-xs">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Date</div>
                  <div>
                    {line?.transaction_date
                      ? new Date(line.transaction_date).toISOString().slice(0, 10)
                      : '—'}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Description</div>
                  <div className="truncate" title={line?.description || ''}>
                    {line?.description || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Line total</div>
                  <div className="font-semibold">{formatMoney(lineAbs)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Allocated</div>
                  <div className="font-semibold">{formatMoney(allocatedSum)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Remainder</div>
                  <div className={
                    balanced
                      ? 'font-semibold text-green-700 dark:text-green-400'
                      : 'font-semibold text-amber-700 dark:text-amber-400'
                  }>
                    {formatMoney(remainder)}
                  </div>
                </div>
              </div>
              {!balanced && (
                <div className="mt-2 flex items-center gap-2 text-[11px] text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-3 w-3" />
                  Allocations must equal {formatMoney(lineAbs)} before you can save.
                  <button
                    type="button"
                    className="ml-2 text-blue-600 underline"
                    onClick={fillRemainderOnLast}
                  >
                    Fill remainder into last row
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {done ? (
            <Card className="border-green-500 bg-green-50 dark:bg-green-950/30">
              <CardContent className="p-4 text-xs text-green-800 dark:text-green-200">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="h-4 w-4" />
                  Match created with {allocations.length} allocation{allocations.length === 1 ? '' : 's'}
                  {done.reconciled ? ' and reconciled' : ' (draft)'}.
                </div>
                <div className="mt-2">
                  <Button size="sm" onClick={() => onOpenChange(false)}>Close</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {error && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <FormLabel>Match type</FormLabel>
                  <Select value={matchType} onChange={(e) => setMatchType(e.target.value)}>
                    {MATCH_TYPES_ALL.map((t) => (
                      <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FormLabel>Match date</FormLabel>
                  <Input
                    type="date"
                    value={matchDate}
                    onChange={(e) => setMatchDate(e.target.value)}
                  />
                </div>
                <div>
                  <FormLabel>Reference</FormLabel>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-3">
                {allocations.map((a, idx) => (
                  <Card key={a._key}>
                    <CardContent className="p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="text-xs font-semibold">Allocation {idx + 1}</div>
                        {allocations.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAlloc(a._key)}
                            className="text-xs text-red-600 hover:underline dark:text-red-400"
                            aria-label={`Remove allocation ${idx + 1}`}
                          >
                            <Trash2 className="mr-1 inline h-3 w-3" />
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="mb-3">
                        <FormLabel>Amount</FormLabel>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={a.allocated_amount}
                          onChange={(e) => updateAlloc(a._key, { allocated_amount: e.target.value })}
                        />
                      </div>
                      <AllocationTargetPicker
                        bankAccountId={bankAccountId}
                        lineAmount={a.allocated_amount || lineAmount}
                        value={a}
                        onChange={(next) => updateAlloc(a._key, next)}
                        idPrefix={`alloc-${idx}-`}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Button variant="outline" size="sm" onClick={addAlloc}>
                <Plus className="mr-1 h-3 w-3" /> Add allocation
              </Button>

              <div>
                <FormLabel>Notes</FormLabel>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal memo"
                />
              </div>

              <div className="flex items-center gap-2 text-xs">
                <input
                  id="split-reconcile-now"
                  type="checkbox"
                  checked={reconcileNow}
                  onChange={(e) => setReconcileNow(e.target.checked)}
                />
                <label htmlFor="split-reconcile-now">
                  Reconcile immediately (otherwise save as draft)
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button onClick={submit} disabled={!canSubmit || busy}>
                  {busy ? 'Saving…' : reconcileNow ? 'Create & reconcile' : 'Create draft match'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </SlideOver>
  );
}

export default SplitAllocationModal;
